// src/components/layout/SidebarNav.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Drawer, List, ListItem, ListItemIcon, ListItemText, Collapse, Divider, Box,
  Select, MenuItem, FormControl, InputLabel, Alert
} from '@mui/material';
import {
  ExpandLess, ExpandMore,
  FileDownload as FileDownloadIcon,
  LiveTv as LiveTvIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  SpaceDashboard as SpaceDashboardIcon,
  Timer as TimerIcon,
  AccountCircle,
  Notifications as NotificationsIcon,
  OpenInNew as OpenInNewIcon,
  Settings as SettingsIcon,
  EventNote as EventNoteIcon,
  Sports as SportsIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  SwapVert as SwapVertIcon,
  FormatListNumbered as FormatListNumberedIcon,
  RecordVoiceOver as RecordVoiceOverIcon,
  UploadFile as UploadFileIcon,
  BackHand as BackHandIcon,
  TouchApp as TouchAppIcon,
  PhotoCameraFront as PhotoCameraFrontIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

// SVGs que ya usas
import { ReactComponent as EventsIcon } from '../../assets/icons/EVENTS.svg';
import { ReactComponent as TeamsIcon } from '../../assets/icons/Teams.svg';
import { ReactComponent as CoachIcon } from '../../assets/icons/Coach.svg';
import { ReactComponent as AthleteIcon } from '../../assets/icons/ATHLETES.svg';
import { ReactComponent as RankingIcon } from '../../assets/icons/Ranking-People-First.svg';
import { ReactComponent as OrderIcon } from '../../assets/icons/Order.svg';
import { ReactComponent as IntegrationIcon } from '../../assets/icons/Integration.svg';
import { ReactComponent as MeetmanagerIcon } from '../../assets/icons/Meet.svg';
import { ReactComponent as JsonIcon } from '../../assets/icons/json.svg';
import { ReactComponent as CsvIcon } from '../../assets/icons/Csv.svg';
import { ReactComponent as ExcelIcon } from '../../assets/icons/Excel.svg';
import { ReactComponent as DashboardIcon } from '../../assets/icons/Dashboard.svg';

import { useNavConfig } from '../../hooks/useNavConfig';
import { ROLE } from '../../config/nav/defaultNav';

const activeStyle = (path, location) => ({
  color: location.pathname === path ? 'primary.main' : 'inherit',
  backgroundColor: location.pathname === path ? 'rgba(0, 0, 0, 0.08)' : 'inherit',
  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
});

const RenderListItem = ({ to, icon, primary, location, sx, onClick }) => (
  <ListItem
    button
    component={Link}
    to={to}
    onClick={onClick}
    sx={{ ...activeStyle(to, location), pl: 6, ...sx }}
  >
    <ListItemIcon>{icon}</ListItemIcon>
    <ListItemText primary={primary} />
  </ListItem>
);

// Mapa de iconos por iconKey
const iconByKey = {
  admin: <AdminPanelSettingsIcon />,
  dashboard: <DashboardIcon />,
  account: <AccountCircle />,
  tools: <IntegrationIcon />,
  events: <EventsIcon />,
  teams: <TeamsIcon />,
  org: <TeamsIcon />,
  coach: <CoachIcon />,
  athletes: <AthleteIcon />,
  timer: <TimerIcon />,
  referee: <SportsIcon />,
  scoreboard: <SpaceDashboardIcon />,
  ranking: <RankingIcon />,
  records: <RecordVoiceOverIcon />,
  results: <RankingIcon />,
  hand: <BackHandIcon />,
  lynx: <PhotoCameraFrontIcon />,
  live: <LiveTvIcon />,
  docs: <DescriptionIcon />,
  assign: <TouchAppIcon />,
  training: <EventNoteIcon />,
  tournament: <SportsIcon />,
  id: <FormatListNumberedIcon />,
  press: <SpaceDashboardIcon />, // cámbialo si tienes un ícono propio
};

// Helper: permisos (si decides usarlos)
const hasPermission = (user, perm) => {
  if (!perm) return true;
  const perms = user?.permissions || [];
  return perms.includes(perm);
};

const SidebarNav = ({ open, onClose }) => {
  const { user } = useAuth();
  const location = useLocation();

  // Carga de secciones nav según rol (Auth + Firestore)
  const { sections, loading, role } = useNavConfig(user);

  // Mock competencias (mantengo tu estructura)
  const competencias = [];
  const loadingCompetencias = false;

  const [openDropdown, setOpenDropdown] = useState(null);
  const [eventosOpen, setEventosOpen] = useState(false);
  const [descargasOpen, setDescargasOpen] = useState(false);
  const [integracionesOpen, setIntegracionesOpen] = useState(false);
  const [modoManualOpen, setModoManualOpen] = useState(false);
  const [finishLynxOpen, setFinishLynxOpen] = useState(false);
  const [hyTekOpen, setHyTekOpen] = useState(false);
  const [subirArchivosOpen, setSubirArchivosOpen] = useState(false);
  const [selectedCompetencia, setSelectedCompetencia] = useState('');

  const toggleDropdown = (id) => () =>
    setOpenDropdown((prev) => (prev === id ? null : id));

  const handleCompetenciaChange = (competenciaId) => setSelectedCompetencia(competenciaId);
  const getEventRoute = (tabKey) => (!selectedCompetencia ? '#' : `/${selectedCompetencia}/${tabKey}`);

  // Auto-selección por URL
  useEffect(() => {
    if (!loadingCompetencias && competencias.length > 0) {
      const pathParts = location.pathname.split('/');
      const possibleCompetenciaId = pathParts[1];
      if (possibleCompetenciaId && competencias.find((c) => c.id === possibleCompetenciaId)) {
        setSelectedCompetencia(possibleCompetenciaId);
      } else if (!selectedCompetencia) {
        setSelectedCompetencia(competencias[0].id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingCompetencias, competencias, location.pathname]);

  return (
    <Drawer anchor="left" open={open} onClose={onClose}>
      <Box sx={{ width: 280, display: 'flex', flexDirection: 'column', height: '100%' }} role="presentation">

        {/* ENLACE PÚBLICO: Eventos */}
        <List>
          <RenderListItem
            to="/eventos"
            icon={<EventsIcon />}
            primary="Eventos"
            location={location}
            onClick={onClose}
            sx={{ pl: 2 }}
          />
        </List>

        <Divider />

        {/* SECCIONES cargadas por rol (Auth + Firestore) */}
        <List>
          {!loading && sections.map((section) => {
            const isOpen = openDropdown === section.id;
            const toggle = () => setOpenDropdown((prev) => (prev === section.id ? null : section.id));

            return (
              <React.Fragment key={section.id}>
                <ListItem button onClick={toggle} aria-expanded={isOpen} aria-controls={`${section.id}-menu`}>
                  <ListItemIcon>{iconByKey[section.iconKey] || <SpaceDashboardIcon />}</ListItemIcon>
                  <ListItemText primary={section.title} />
                  {isOpen ? <ExpandLess /> : <ExpandMore />}
                </ListItem>

                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {(section.items || []).filter(it => hasPermission(user, it.permission)).map((it) => (
                      <ListItem
                        key={it.to}
                        button
                        component={Link}
                        to={it.to}
                        onClick={onClose}
                        sx={{ ...activeStyle(it.to, location), pl: 6 }}
                      >
                        <ListItemIcon>{iconByKey[it.iconKey] || <SpaceDashboardIcon />}</ListItemIcon>
                        <ListItemText primary={it.label} />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </React.Fragment>
            );
          })}
        </List>

        {/* BLOQUE DINÁMICO para ORGANIZADOR: "Gestión" con selector de competencia */}
        {role === ROLE.ORGANIZADOR && (
          <>
            <Divider />
            <List>
              <ListItem
                button
                onClick={toggleDropdown('gestion')}
                aria-expanded={openDropdown === 'gestion'}
                aria-controls="gestion-menu"
              >
                <ListItemIcon><IntegrationIcon /></ListItemIcon>
                <ListItemText primary="Gestión" />
                {openDropdown === 'gestion' ? <ExpandLess /> : <ExpandMore />}
              </ListItem>

              <Collapse in={openDropdown === 'gestion'} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>

                  {/* Eventos con selector de competencia */}
                  <ListItem
                    button
                    onClick={() => setEventosOpen((prev) => !prev)}
                    aria-expanded={eventosOpen}
                    aria-controls="eventos-menu"
                    sx={{ pl: 2 }}
                  >
                    <ListItemIcon><EventsIcon /></ListItemIcon>
                    <ListItemText primary="Eventos" />
                    {eventosOpen ? <ExpandLess /> : <ExpandMore />}
                  </ListItem>

                  <Collapse in={eventosOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>

                      <ListItem sx={{ pl: 4, pr: 2, pb: 2 }}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Competencia</InputLabel>
                          {loadingCompetencias ? (
                            <Select disabled label="Competencia">
                              <MenuItem value="">Cargando...</MenuItem>
                            </Select>
                          ) : (
                            <Select
                              value={selectedCompetencia}
                              label="Competencia"
                              onChange={(e) => handleCompetenciaChange(e.target.value)}
                            >
                              <MenuItem value="">
                                <em>Selecciona una competencia</em>
                              </MenuItem>
                              {competencias.map((c) => (
                                <MenuItem key={c.id} value={c.id}>
                                  {c.general?.nombre || c.id.slice(0, 8)}
                                </MenuItem>
                              ))}
                            </Select>
                          )}
                        </FormControl>
                      </ListItem>

                      {!selectedCompetencia && (
                        <ListItem sx={{ pl: 4, pr: 2 }}>
                          <Alert severity="info" sx={{ width: '100%', fontSize: '0.75rem' }}>
                            Selecciona una competencia para acceder a las opciones
                          </Alert>
                        </ListItem>
                      )}

                      {selectedCompetencia && (
                        <>
                          {[
                            { to: `/${selectedCompetencia}/ajustes`, label: 'Ajustes', icon: <SettingsIcon /> },
                            { to: `/${selectedCompetencia}/convocatoria`, label: 'Convocatoria', icon: <DescriptionIcon /> },
                            { to: `/${selectedCompetencia}/pruebas`, label: 'Pruebas', icon: <EventNoteIcon /> },
                            { to: `/${selectedCompetencia}/competidores`, label: 'Competidores', icon: <SportsIcon /> },
                            { to: `/${selectedCompetencia}/equipos`, label: 'Equipos', icon: <PersonIcon /> },
                            { to: `/${selectedCompetencia}/horarios`, label: 'Horarios', icon: <ScheduleIcon /> },
                            { to: `/${selectedCompetencia}/siembra`, label: 'Siembra', icon: <SwapVertIcon /> },
                            { to: `/${selectedCompetencia}/jueces`, label: 'Jueces', icon: <TimerIcon /> },
                            { to: `/${selectedCompetencia}/numeracion`, label: 'Numeración', icon: <FormatListNumberedIcon /> },
                            { to: `/${selectedCompetencia}/records`, label: 'Records', icon: <RecordVoiceOverIcon /> },
                            { to: `/${selectedCompetencia}/integraciones`, label: 'Integraciones', icon: <IntegrationIcon /> },
                            { to: `/${selectedCompetencia}/notificaciones`, label: 'Notificaciones', icon: <NotificationsIcon /> },
                            { to: `/${selectedCompetencia}/vistaprevia`, label: 'Vista Previa', icon: <OpenInNewIcon /> },
                          ].map((it) => (
                            <ListItem
                              key={it.to}
                              button
                              component={Link}
                              to={it.to}
                              onClick={onClose}
                              sx={{ ...activeStyle(it.to, location), pl: 6 }}
                            >
                              <ListItemIcon>{it.icon}</ListItemIcon>
                              <ListItemText primary={it.label} />
                            </ListItem>
                          ))}
                        </>
                      )}
                    </List>
                  </Collapse>

                  {/* En Vivo */}
                  <ListItem
                    button
                    component={Link}
                    to="/en-vivo"
                    onClick={onClose}
                    sx={{ ...activeStyle('/en-vivo', location), pl: 2 }}
                  >
                    <ListItemIcon><LiveTvIcon /></ListItemIcon>
                    <ListItemText primary="En Vivo" />
                  </ListItem>

                  {/* Descargas */}
                  <ListItem
                    button
                    onClick={() => setDescargasOpen((p) => !p)}
                    aria-expanded={descargasOpen}
                    aria-controls="descargas-menu"
                    sx={{ pl: 2 }}
                  >
                    <ListItemIcon><FileDownloadIcon /></ListItemIcon>
                    <ListItemText primary="Descargas" />
                    {descargasOpen ? <ExpandLess /> : <ExpandMore />}
                  </ListItem>
                  <Collapse in={descargasOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {[
                        { to: '/descargas/programa', label: 'Programa', icon: <EventsIcon /> },
                        { to: '/descargas/lista-atletas', label: 'Lista de Atletas', icon: <AthleteIcon /> },
                        { to: '/descargas/lista-inscritos', label: 'Lista de Inscritos', icon: <OrderIcon /> },
                        { to: '/descargas/siembra', label: 'Siembra', icon: <OrderIcon /> },
                        { to: '/descargas/resultados', label: 'Resultados', icon: <RankingIcon /> },
                      ].map((d) => (
                        <ListItem
                          key={d.to}
                          button
                          component={Link}
                          to={d.to}
                          onClick={onClose}
                          sx={{ ...activeStyle(d.to, location), pl: 6 }}
                        >
                          <ListItemIcon>{d.icon}</ListItemIcon>
                          <ListItemText primary={d.label} />
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>

                  {/* Subir Archivos */}
                  <ListItem
                    button
                    onClick={() => setSubirArchivosOpen((p) => !p)}
                    aria-expanded={subirArchivosOpen}
                    aria-controls="subir-archivos-menu"
                    sx={{ pl: 2 }}
                  >
                    <ListItemIcon><UploadFileIcon /></ListItemIcon>
                    <ListItemText primary="Subir Archivos" />
                    {subirArchivosOpen ? <ExpandLess /> : <ExpandMore />}
                  </ListItem>
                  <Collapse in={subirArchivosOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {[
                        { to: '/subir-archivos/excel', label: 'Subir Excel', icon: <ExcelIcon /> },
                        { to: '/subir-archivos/csv', label: 'Subir CSV', icon: <CsvIcon /> },
                        { to: '/subir-archivos/json', label: 'Subir JSON', icon: <JsonIcon /> },
                      ].map((u) => (
                        <ListItem
                          key={u.to}
                          button
                          component={Link}
                          to={u.to}
                          onClick={onClose}
                          sx={{ ...activeStyle(u.to, location), pl: 6 }}
                        >
                          <ListItemIcon>{u.icon}</ListItemIcon>
                          <ListItemText primary={u.label} />
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>

                  {/* Integraciones */}
                  <ListItem
                    button
                    onClick={() => setIntegracionesOpen((p) => !p)}
                    aria-expanded={integracionesOpen}
                    aria-controls="integraciones-menu"
                    sx={{ pl: 2 }}
                  >
                    <ListItemIcon><IntegrationIcon /></ListItemIcon>
                    <ListItemText primary="Integraciones" />
                    {integracionesOpen ? <ExpandLess /> : <ExpandMore />}
                  </ListItem>
                  <Collapse in={integracionesOpen} timeout="auto" unmountOnExit>
                    {/* Modo Manual */}
                    <ListItem
                      button
                      onClick={() => setModoManualOpen((p) => !p)}
                      aria-expanded={modoManualOpen}
                      aria-controls="modo-manual-menu"
                      sx={{ pl: 4 }}
                    >
                      <ListItemIcon><BackHandIcon /></ListItemIcon>
                      <ListItemText primary="Modo Manual" />
                      {modoManualOpen ? <ExpandLess /> : <ExpandMore />}
                    </ListItem>
                    <Collapse in={modoManualOpen} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {[
                          { to: '/integraciones/modo-manual/gestion', label: 'Gestión Manual', icon: <TouchAppIcon /> },
                          { to: '/integraciones/modo-manual/tableros', label: 'Tableros', icon: <SpaceDashboardIcon /> },
                        ].map((m) => (
                          <ListItem
                            key={m.to}
                            button
                            component={Link}
                            to={m.to}
                            onClick={onClose}
                            sx={{ ...activeStyle(m.to, location), pl: 6 }}
                          >
                            <ListItemIcon>{m.icon}</ListItemIcon>
                            <ListItemText primary={m.label} />
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>

                    {/* FinishLynx */}
                    <ListItem
                      button
                      onClick={() => setFinishLynxOpen((p) => !p)}
                      aria-expanded={finishLynxOpen}
                      aria-controls="finishlynx-menu"
                      sx={{ pl: 4 }}
                    >
                      <ListItemIcon><PhotoCameraFrontIcon /></ListItemIcon>
                      <ListItemText primary="FinishLynx" />
                      {finishLynxOpen ? <ExpandLess /> : <ExpandMore />}
                    </ListItem>
                    <Collapse in={finishLynxOpen} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {[
                          { to: '/integraciones/finishlynx/gestion', label: 'FinishLynx', icon: <DescriptionIcon /> },
                          { to: '/integraciones/finishlynx/tableros', label: 'Tableros', icon: <SpaceDashboardIcon /> },
                        ].map((f) => (
                          <ListItem
                            key={f.to}
                            button
                            component={Link}
                            to={f.to}
                            onClick={onClose}
                            sx={{ ...activeStyle(f.to, location), pl: 6 }}
                          >
                            <ListItemIcon>{f.icon}</ListItemIcon>
                            <ListItemText primary={f.label} />
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>

                    {/* Hy-Tek */}
                    <ListItem
                      button
                      onClick={() => setHyTekOpen((p) => !p)}
                      aria-expanded={hyTekOpen}
                      aria-controls="hytek-menu"
                      sx={{ pl: 4 }}
                    >
                      <ListItemIcon><MeetmanagerIcon /></ListItemIcon>
                      <ListItemText primary="Hy-Tek (MeetManager)" />
                      {hyTekOpen ? <ExpandLess /> : <ExpandMore />}
                    </ListItem>
                    <Collapse in={hyTekOpen} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {[
                          { to: '/integraciones/hytek/gestion', label: 'Hy-Tek (MeetManager)', icon: <DescriptionIcon /> },
                          { to: '/integraciones/hytek/tableros', label: 'Tableros', icon: <SpaceDashboardIcon /> },
                        ].map((h) => (
                          <ListItem
                            key={h.to}
                            button
                            component={Link}
                            to={h.to}
                            onClick={onClose}
                            sx={{ ...activeStyle(h.to, location), pl: 6 }}
                          >
                            <ListItemIcon>{h.icon}</ListItemIcon>
                            <ListItemText primary={h.label} />
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  </Collapse>
                </List>
              </Collapse>
            </List>
          </>
        )}

        <Divider sx={{ mt: 'auto' }} />

        {/* Inferior público */}
        <List>
          <RenderListItem to="/acerca-de" icon={<DescriptionIcon />} primary="Acerca de" location={location} onClick={onClose} sx={{ pl: 2 }} />
          <RenderListItem to="/ayuda" icon={<DescriptionIcon />} primary="Ayuda" location={location} onClick={onClose} sx={{ pl: 2 }} />
          <RenderListItem to="/caracteristicas-precios" icon={<SpaceDashboardIcon />} primary="Características y Precios" location={location} onClick={onClose} sx={{ pl: 2 }} />
        </List>
      </Box>
    </Drawer>
  );
};

export default SidebarNav;
